import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { APP_ROUTES } from '../../../core/constants/app-routes';
import { FLASH_MESSAGES } from '../../../core/constants/flash-messages';
import {
  apiErrorMessage,
  apiErrorMessageAsync,
} from '../../../core/http/api-error-message';
import { BienService } from '../../../core/services/bien.service';
import { DocumentTechniqueService } from '../../../core/services/document-technique.service';
import { FlashService } from '../../../core/services/flash.service';
import { BienImmobilier } from '../../../core/models/bien-immobilier.model';
import {
  formatDateFr,
  Logement,
  toDateIso,
  uniteDetails,
  uniteLabel,
} from '../../../core/models/logement.model';
import {
  DOCUMENT_TECHNIQUE_RESULTAT,
  DocumentTechniqueLigne,
  DocumentTechniqueResultat,
  DocumentTechniqueType,
  isLigneDossierTechniqueOk,
  lignesDossierTechnique,
  requiresPresenceAbsence,
} from '../../../core/models/document-technique.model';
import { DocumentViewerSession } from '../../../core/utils/document-viewer.util';
import { DocumentDeleteSession } from '../../../core/utils/document-delete.util';
import { DocumentDeleteDialogComponent } from '../../../shared/document-delete-dialog/document-delete-dialog.component';
import { DocumentViewerDialogComponent } from '../../../shared/document-viewer-dialog/document-viewer-dialog.component';

/**
 * Page dossier technique d'une unité : liste des types, upload, aperçu,
 * suppression et édition d'expiration.
 */
@Component({
  selector: 'app-dossier-technique',
  templateUrl: './dossier-technique.component.html',
  styleUrls: ['./dossier-technique.component.css'],
  imports: [
    RouterLink,
    FaIconComponent,
    TranslateModule,
    ReactiveFormsModule,
    DocumentViewerDialogComponent,
    DocumentDeleteDialogComponent,
  ],
})
export class DossierTechniqueComponent implements OnInit, OnDestroy {
  @ViewChild('uploadDialog')
  private readonly uploadDialog?: ElementRef<HTMLDialogElement>;

  @ViewChild(DocumentDeleteDialogComponent)
  private readonly deleteDialog?: DocumentDeleteDialogComponent;

  @ViewChild('expirationDialog')
  private readonly expirationDialog?: ElementRef<HTMLDialogElement>;

  @ViewChild(DocumentViewerDialogComponent)
  private readonly viewerDialog?: DocumentViewerDialogComponent;

  readonly routes = APP_ROUTES;
  readonly resultats = Object.values(DOCUMENT_TECHNIQUE_RESULTAT);

  bien?: BienImmobilier;
  logement?: Logement;
  lignes: DocumentTechniqueLigne[] = [];
  loading = true;
  /** Clé i18n d’erreur de chargement page (`loadError` / `notFound`). */
  errorKey: string | null = null;
  listReloadError = false;

  uploadType?: DocumentTechniqueType;
  uploadFile?: File;
  uploading = false;
  uploadErrorMessage: string | null = null;
  uploadFileError = false;

  readonly deleteSession = new DocumentDeleteSession<DocumentTechniqueLigne>();

  expirationLigne?: DocumentTechniqueLigne;
  savingExpiration = false;
  expirationErrorMessage: string | null = null;

  readonly viewer: DocumentViewerSession;

  readonly uploadForm = this.fb.nonNullable.group({
    dateDiagnostic: ['', Validators.required],
    resultat: ['' as DocumentTechniqueResultat | ''],
  });

  readonly expirationForm = this.fb.nonNullable.group({
    dateExpiration: [''],
    unlimited: [false],
  });

  private bienId?: string;
  private logementId?: string;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly bienService: BienService,
    private readonly documentTechniqueService: DocumentTechniqueService,
    private readonly flashService: FlashService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    sanitizer: DomSanitizer
  ) {
    this.viewer = new DocumentViewerSession(sanitizer);
  }

  ngOnInit(): void {
    this.bienId = this.route.snapshot.paramMap.get('bienId') ?? undefined;
    this.logementId =
      this.route.snapshot.paramMap.get('logementId') ?? undefined;
    if (!this.bienId || !this.logementId) {
      this.errorKey = 'dossierTechnique.notFound';
      this.loading = false;
      return;
    }
    this.reload();
  }

  ngOnDestroy(): void {
    this.viewer.revoke();
  }

  get backToBien(): string {
    return this.bienId
      ? `${APP_ROUTES.logements}/${this.bienId}`
      : APP_ROUTES.logements;
  }

  get titreUnite(): string {
    return this.logement ? uniteLabel(this.logement) : '';
  }

  get detailsUnite(): string {
    return this.logement ? uniteDetails(this.logement) : '';
  }

  get uploadNeedsResultat(): boolean {
    return requiresPresenceAbsence(this.uploadType);
  }

  get uploadIsReplace(): boolean {
    return !!this.lignes.find((ligne) => ligne.type === this.uploadType)
      ?.document;
  }

  get isBusy(): boolean {
    return (
      this.uploading ||
      this.deleteSession.deleting ||
      this.savingExpiration ||
      this.viewer.loading
    );
  }

  get deleteTypeLabel(): string {
    return this.deleteSession.ligne?.type ?? '';
  }

  get deleteFileName(): string {
    return this.deleteSession.ligne?.document?.fileNameDto?.trim() || '';
  }

  get expirationTypeLabel(): string {
    return this.expirationLigne?.type ?? '';
  }

  get expirationUnlimited(): boolean {
    return this.expirationForm.controls.unlimited.value;
  }

  fileLabel(ligne: DocumentTechniqueLigne): string {
    return ligne.document?.fileNameDto?.trim() || '';
  }

  diagnosticLabel(ligne: DocumentTechniqueLigne): string {
    return formatDateFr(ligne.document?.dateDiagnosticDto);
  }

  expirationLabel(ligne: DocumentTechniqueLigne): string {
    return formatDateFr(ligne.document?.dateExpirationDto);
  }

  resultatLabel(ligne: DocumentTechniqueLigne): string {
    const resultat = ligne.document?.resultatDto;
    return resultat ? `dossierTechnique.resultats.${resultat}` : '';
  }

  nonApplicableHint(ligne: DocumentTechniqueLigne): string {
    return ligne.nonApplicableReason
      ? `dossierTechnique.nonApplicable.${ligne.nonApplicableReason}`
      : 'dossierTechnique.nonApplicable.generic';
  }

  ligneOk(ligne: DocumentTechniqueLigne): boolean {
    return isLigneDossierTechniqueOk(ligne);
  }

  openViewer(ligne: DocumentTechniqueLigne): void {
    const document = ligne.document;
    const documentId = document?.idDocumentDto;
    if (!this.logementId || !document || !documentId) {
      return;
    }

    this.viewer.open({
      title: ligne.type,
      fileName: document.fileNameDto,
      contentType: document.contentTypeDto,
      download$: this.documentTechniqueService.download(
        this.logementId,
        documentId
      ),
      dialog: this.viewerDialog?.dialogElement,
      messages: this.viewerMessages(),
      resolveErrorAsync: (err) =>
        this.resolveApiErrorAsync(err, 'dossierTechnique.viewError'),
    });
  }

  closeViewer(): void {
    this.viewer.close(this.viewerDialog?.dialogElement);
  }

  onViewerDialogCancel(event: Event): void {
    this.viewer.onDialogCancel(event, this.viewerDialog?.dialogElement);
  }

  openViewerExternally(): void {
    this.viewer.openExternally(
      this.translate.instant('dossierTechnique.viewError')
    );
  }

  downloadViewerFile(): void {
    this.viewer.downloadFile(
      this.translate.instant('dossierTechnique.documentFallback')
    );
  }

  openUpload(ligne: DocumentTechniqueLigne): void {
    if (!ligne.exigible) {
      return;
    }
    this.uploadType = ligne.type;
    this.uploadFile = undefined;
    this.uploadErrorMessage = null;
    this.uploadFileError = false;
    this.uploading = false;
    this.uploadForm.reset({
      dateDiagnostic: '',
      resultat: '',
    });
    if (this.uploadNeedsResultat) {
      this.uploadForm.controls.resultat.setValidators([Validators.required]);
    } else {
      this.uploadForm.controls.resultat.clearValidators();
    }
    this.uploadForm.controls.resultat.updateValueAndValidity();
    this.uploadDialog?.nativeElement.showModal();
  }

  cancelUpload(): void {
    this.uploadDialog?.nativeElement.close();
    this.uploadType = undefined;
    this.uploadFile = undefined;
    this.uploadErrorMessage = null;
    this.uploadFileError = false;
    this.uploading = false;
  }

  onUploadDialogCancel(event: Event): void {
    event.preventDefault();
    this.cancelUpload();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.uploadFile = file;
    this.uploadFileError = false;
    this.uploadErrorMessage = null;
  }

  submitUpload(): void {
    if (!this.logementId || !this.uploadType) {
      return;
    }
    this.uploadForm.markAllAsTouched();
    this.uploadFileError = !this.uploadFile;
    if (this.uploadForm.invalid || !this.uploadFile) {
      return;
    }

    const dateDiagnostic = this.uploadForm.controls.dateDiagnostic.value;
    const resultatRaw = this.uploadForm.controls.resultat.value;
    const resultat = this.uploadNeedsResultat
      ? (resultatRaw as DocumentTechniqueResultat)
      : null;

    this.uploading = true;
    this.uploadErrorMessage = null;

    this.documentTechniqueService
      .upload(this.logementId, {
        type: this.uploadType,
        dateDiagnostic,
        resultat,
        file: this.uploadFile,
      })
      .subscribe({
        next: () => {
          this.flashService.success(FLASH_MESSAGES.documentTechniqueUploade);
          this.cancelUpload();
          this.reloadDocuments();
        },
        error: (err) => {
          this.uploading = false;
          this.uploadErrorMessage = this.resolveApiError(
            err,
            'dossierTechnique.uploadError'
          );
        },
      });
  }

  askDelete(ligne: DocumentTechniqueLigne): void {
    this.deleteSession.ask(
      ligne,
      ligne.document?.idDocumentDto,
      this.isBusy,
      this.deleteDialog
    );
  }

  cancelDelete(): void {
    this.deleteSession.cancel(this.deleteDialog);
  }

  onDeleteDialogCancel(event: Event): void {
    this.deleteSession.onDialogCancel(event, this.deleteDialog);
  }

  confirmDelete(): void {
    const documentId = this.deleteSession.ligne?.document?.idDocumentDto;
    if (!this.logementId || !documentId) {
      return;
    }

    this.deleteSession.startRequest();

    this.documentTechniqueService.delete(this.logementId, documentId).subscribe({
      next: () => {
        this.flashService.success(FLASH_MESSAGES.documentTechniqueSupprime);
        this.cancelDelete();
        this.reloadDocuments();
      },
      error: (err) => {
        this.deleteSession.fail(
          this.resolveApiError(err, 'dossierTechnique.deleteError')
        );
      },
    });
  }

  openExpiration(ligne: DocumentTechniqueLigne): void {
    if (!ligne.document?.idDocumentDto || this.isBusy) {
      return;
    }
    const current = toDateIso(ligne.document.dateExpirationDto);
    this.expirationLigne = ligne;
    this.savingExpiration = false;
    this.expirationErrorMessage = null;
    this.expirationForm.reset({
      dateExpiration: current,
      unlimited: !current,
    });
    this.syncExpirationDateState();
    this.expirationDialog?.nativeElement.showModal();
  }

  cancelExpiration(): void {
    this.expirationDialog?.nativeElement.close();
    this.expirationLigne = undefined;
    this.savingExpiration = false;
    this.expirationErrorMessage = null;
  }

  onExpirationDialogCancel(event: Event): void {
    event.preventDefault();
    this.cancelExpiration();
  }

  onUnlimitedChange(): void {
    this.syncExpirationDateState();
  }

  submitExpiration(): void {
    const documentId = this.expirationLigne?.document?.idDocumentDto;
    if (!this.logementId || !documentId) {
      return;
    }

    const unlimited = this.expirationForm.controls.unlimited.value;
    const dateExpiration = this.expirationForm.controls.dateExpiration.value;
    if (!unlimited && !dateExpiration) {
      this.expirationForm.controls.dateExpiration.markAsTouched();
      this.expirationForm.controls.dateExpiration.setErrors({ required: true });
      return;
    }

    this.savingExpiration = true;
    this.expirationErrorMessage = null;

    this.documentTechniqueService
      .updateExpiration(
        this.logementId,
        documentId,
        unlimited ? null : dateExpiration
      )
      .subscribe({
        next: () => {
          this.flashService.success(
            FLASH_MESSAGES.documentTechniqueExpirationMaj
          );
          this.cancelExpiration();
          this.reloadDocuments();
        },
        error: (err) => {
          this.savingExpiration = false;
          this.expirationErrorMessage = this.resolveApiError(
            err,
            'dossierTechnique.expirationError'
          );
        },
      });
  }

  private syncExpirationDateState(): void {
    const dateControl = this.expirationForm.controls.dateExpiration;
    if (this.expirationForm.controls.unlimited.value) {
      dateControl.setValue('');
      dateControl.disable({ emitEvent: false });
      dateControl.setErrors(null);
    } else {
      dateControl.enable({ emitEvent: false });
    }
  }

  private viewerMessages() {
    return {
      viewError: this.translate.instant('dossierTechnique.viewError'),
      viewPdfMobile: this.translate.instant('dossierTechnique.viewPdfMobile'),
      documentFallback: this.translate.instant(
        'dossierTechnique.documentFallback'
      ),
    };
  }

  private resolveApiError(err: unknown, fallbackKey: string): string {
    return apiErrorMessage(err, this.translate.instant(fallbackKey));
  }

  private resolveApiErrorAsync(
    err: unknown,
    fallbackKey: string
  ): Promise<string> {
    return apiErrorMessageAsync(err, this.translate.instant(fallbackKey));
  }

  private reload(): void {
    if (!this.bienId || !this.logementId) {
      return;
    }
    this.loading = true;
    this.errorKey = null;
    this.listReloadError = false;
    forkJoin({
      bien: this.bienService.findById(this.bienId),
      documents: this.documentTechniqueService.list(this.logementId),
    }).subscribe({
      next: ({ bien, documents }) => {
        const logement = (bien.logementsDto ?? []).find(
          (item) => item.idLogementDto === this.logementId
        );
        if (!logement) {
          this.errorKey = 'dossierTechnique.notFound';
          this.loading = false;
          return;
        }
        this.bien = bien;
        this.logement = logement;
        this.lignes = lignesDossierTechnique(
          documents ?? [],
          bien.anneeConstructionDto,
          logement.typeUniteDto,
          logement.chauffageDto
        );
        this.loading = false;
      },
      error: () => {
        this.errorKey = 'dossierTechnique.loadError';
        this.loading = false;
      },
    });
  }

  private reloadDocuments(): void {
    if (!this.logementId) {
      return;
    }
    this.listReloadError = false;
    this.documentTechniqueService.list(this.logementId).subscribe({
      next: (documents) => {
        this.lignes = lignesDossierTechnique(
          documents ?? [],
          this.bien?.anneeConstructionDto,
          this.logement?.typeUniteDto,
          this.logement?.chauffageDto
        );
      },
      error: () => {
        this.listReloadError = true;
      },
    });
  }
}
